import { head, isEmpty, min } from 'lodash/fp';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ReservationStockStatus } from '../../../../api';
import { type LabelsDataFragment } from '../../../../api/fragments/LabelsData';
import { type ModelConfiguratorListDataFragment } from '../../../../api/fragments/ModelConfiguratorListData';
import { StyledCard } from '../../../../components/card/ui';
import { useThemeComponents } from '../../../../themes';
import useCompanyFormats from '../../../../utilities/useCompanyFormats';
import usePublic from '../../../../utilities/usePublic';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import { StyledLabel } from '../ModelConfiguratorDetailsPage/VehicleCard';
import { StyledLabelContainer, StyledVehicleCard } from './ui';

const Container = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;

    & > * {
        flex: 1 1 150px;
    }

    & .ant-btn {
        min-width: fit-content;
    }

    & div:has(> p-button) {
        min-width: fit-content;

        & > p-button {
            width: 100%;
        }
    }
`;

type VehicleCardProps = {
    model: ModelConfiguratorListDataFragment;
    hasLabel: boolean;
    setHasLabel: Dispatch<SetStateAction<boolean>>;
    label?: LabelsDataFragment[];
};

const VehicleListData = ({ model, label, setHasLabel, hasLabel }: VehicleCardProps) => {
    const { t } = useTranslation(['configuratorListVehicle']);
    const { formatAmountWithCurrency } = useCompanyFormats(
        model.module.__typename === 'ConfiguratorModule' && model.module.companyId
    );
    const { Button } = useThemeComponents();
    const translate = useTranslatedString();

    const fallbackSrc = usePublic('empty.svg');

    const availableVariantBasedOnInvStock = useMemo(() => {
        if (!model.variantConfigurators || model.variantConfigurators.length === 0) {
            return null;
        }

        return model.variantConfigurators.filter(variantConfigurator =>
            variantConfigurator.inventories.some(inventory => inventory.availableStock > 0)
        );
    }, [model.variantConfigurators]);

    const [firstVariant, variantLowestPrice] = useMemo(() => {
        if (availableVariantBasedOnInvStock) {
            const firstVariant = head(
                availableVariantBasedOnInvStock.toSorted((a, b) => {
                    if (a?.variant?.__typename === 'LocalVariant' && b?.variant?.__typename === 'LocalVariant') {
                        return a.variant.vehiclePrice - b.variant.vehiclePrice;
                    }

                    return 0;
                })
            )?.variant;

            if (firstVariant?.__typename === 'LocalVariant') {
                const availablePriceList = availableVariantBasedOnInvStock
                    .flatMap(inventory => inventory.inventories)
                    .filter(inv =>
                        inv.stocks.some(
                            stock =>
                                stock.__typename === 'ConfiguratorStockInventory' &&
                                stock.reservationStatus === ReservationStockStatus.Available
                        )
                    )
                    .flatMap(stock => stock.price);

                const variantLowestPrice = min(availablePriceList) ?? 0;

                return [firstVariant, variantLowestPrice];
            }
        }

        return [null, null];
    }, [availableVariantBasedOnInvStock]);

    const variantConfigurator = useMemo(
        () => model.variantConfigurators.find(variant => variant.variant.id === firstVariant?.id),
        [firstVariant?.id, model.variantConfigurators]
    );

    const image = useMemo(() => firstVariant?.images?.[0]?.url || fallbackSrc, [fallbackSrc, firstVariant]);

    const handleClickOverview = useCallback(() => {
        window.open(`//${model.externalUrl}`, 'model_overview_window');
    }, [model.externalUrl]);

    const modelName = translate(model.model.name);

    const labelDisplay = useCallback(
        (id: string) => {
            const currentLabel = label.find(labelItem => labelItem.id === id);

            return (
                (currentLabel?.moduleVariants || []).find(moduleVariant =>
                    (moduleVariant.addedVariants || []).some(variant =>
                        (model.variantConfigurators || []).some(
                            variantConfigurator =>
                                variantConfigurator.variant.__typename === 'LocalVariant' &&
                                variantConfigurator.variant.versioning.suiteId === variant.versioning.suiteId
                        )
                    )
                ) && currentLabel?.isActive
            );
        },
        [label, model.variantConfigurators]
    );

    useEffect(() => {
        if (labelDisplay) {
            setHasLabel(true);
        }
    }, [hasLabel, labelDisplay, setHasLabel]);

    if (!variantConfigurator || variantConfigurator.__typename !== 'VariantConfigurator') {
        console.error('variant configurator is invalid or not in expected type');

        return null;
    }

    return (
        <StyledVehicleCard
            actions={[
                <Container>
                    {!isEmpty(model.externalUrl) && (
                        <Button onClick={handleClickOverview} type="tertiary" block>
                            {t('configuratorListVehicle:actions.modelOverview')}
                        </Button>
                    )}
                    <Link to={`./details/${model.urlIdentifier}`}>
                        <Button className="ReserveNow" data-model={model.model.name.defaultValue} type="primary" block>
                            {t('configuratorListVehicle:actions.reserve')}
                        </Button>
                    </Link>
                </Container>,
            ]}
            cover={<img alt={modelName} src={image} />}
        >
            <StyledCard.Meta
                description={
                    <>
                        {t('configuratorListVehicle:labels.startingFrom', {
                            amount: formatAmountWithCurrency(variantLowestPrice),
                        })}
                        <StyledLabelContainer isLabel={hasLabel}>
                            {label.map(
                                labelItem =>
                                    labelDisplay(labelItem.id) && (
                                        <StyledLabel
                                            style={{
                                                backgroundColor: `#${labelItem.backgroundColor}`,
                                                color: `#${labelItem.textColor}`,
                                            }}
                                        >
                                            {translate(labelItem.labelName)}
                                        </StyledLabel>
                                    )
                            )}
                        </StyledLabelContainer>
                    </>
                }
                title={modelName}
            />
        </StyledVehicleCard>
    );
};

export default VehicleListData;
